import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Button, Card, Input } from '../../components/ui'
import { Footer } from '../../components/Footer'
import { GhostMark } from '../../components/GhostMark'
import { IconCheck, IconLock, IconShield } from '../../components/icons'
import { bindHolderKey, confirmPhone, startPhone, verifyInfo, type VerifyInfo } from '../../lib/verifyApi'
import './auth.css'

// The candidate-facing page for an emailed self-verify link (/self-verify?token=…).
// It's public — the candidate isn't a console user; the link's token is the only
// credential. It runs on ANY device (Android / desktop / iPhone), which is how the
// privacy promise stays inclusive: the steps are platform-portable.
//
//   1. Device key  — register a WebAuthn passkey as the holder key (skipped where
//                    WebAuthn is unavailable). This is web/software custody — no
//                    Secure Enclave — so it honestly caps the credential at Tier 2.
//   2. Phone       — Telesign risk check + SMS code (the recruiter never sees the
//                    number, only that it checked out).
//   3. Done        — the achieved Verified Mark + an upsell to the iOS app, which
//                    re-mints the proofs in hardware custody to unlock Tier 3.

type Stage = 'holder' | 'phone' | 'code' | 'done'

const webauthnSupported = () => typeof window !== 'undefined' && !!window.PublicKeyCredential

export function SelfVerify() {
  const [params] = useSearchParams()
  const token = params.get('token') || ''

  const [info, setInfo] = useState<VerifyInfo | null>(null)
  const [loadError, setLoadError] = useState('')
  const [stage, setStage] = useState<Stage>('holder')
  const [keyBound, setKeyBound] = useState(false)

  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [maskedPhone, setMaskedPhone] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // Resolve the request on mount. A missing/expired token is a dead end. We open
  // on the device-key step only when WebAuthn is available and no key is bound yet;
  // otherwise we go straight to phone (the web proof still lands, just without the
  // web_custody marker).
  useEffect(() => {
    if (!token) {
      setLoadError('This verification link is missing its token. Ask whoever invited you to resend it.')
      return
    }
    let cancelled = false
    verifyInfo(token)
      .then((i) => {
        if (cancelled) return
        setInfo(i)
        const alreadyBound = i.holderBound || i.custody !== 'none'
        setKeyBound(alreadyBound)
        setStage(alreadyBound || !webauthnSupported() ? 'phone' : 'holder')
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'This link is invalid or expired.')
      })
    return () => {
      cancelled = true
    }
  }, [token])

  async function onBindKey() {
    setError('')
    setLoading(true)
    try {
      await bindHolderKey(token)
      setKeyBound(true)
      setStage('phone')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set up your device key. You can skip this step.')
    } finally {
      setLoading(false)
    }
  }

  async function onSendCode(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (phone.replace(/[^\d]/g, '').length < 8) {
      return setError('Enter your full phone number, including country code.')
    }
    setLoading(true)
    try {
      const { maskedPhone } = await startPhone(token, phone.trim())
      setMaskedPhone(maskedPhone)
      setStage('code')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send a code. Try again.')
    } finally {
      setLoading(false)
    }
  }

  async function onConfirm(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (code.replace(/[^\d]/g, '').length < 6) return setError('Enter the 6-digit code we texted you.')
    setLoading(true)
    try {
      await confirmPhone(token, code.trim())
      setStage('done')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not verify that code. Try again.')
    } finally {
      setLoading(false)
    }
  }

  const invitedBy = info?.company ? `${info.company} invited you to ` : ''

  return (
    <div className="auth">
      <Card className="auth__card">
        <div className="auth__logo">
          <GhostMark size={64} glow breathing />
        </div>

        {loadError ? (
          <>
            <h1 className="auth__title">Link unavailable</h1>
            <p className="auth__sub">{loadError}</p>
            <p className="auth__foot">
              Recruiting on Vouchr? <Link to="/login">Sign in</Link>
            </p>
          </>
        ) : !info ? (
          <p className="auth__sub" style={{ textAlign: 'center' }}>Loading your verification…</p>
        ) : stage === 'done' ? (
          <>
            <div style={{ textAlign: 'center', color: 'var(--cred-live)', marginBottom: '0.5rem' }}>
              <IconCheck size={40} />
            </div>
            <h1 className="auth__title">You're verified</h1>
            <p className="auth__sub">
              Thanks, {info.name}. Your phone number is verified{keyBound ? ' and secured to this device' : ''}, and
              {info.company ? ` ${info.company}` : ' the recruiter'} has been notified. We never shared your number with
              them.
            </p>
            <div className="auth__note" style={{ display: 'block', marginTop: '0.5rem' }}>
              <strong style={{ color: 'var(--text)' }}>Want the strongest Verified Mark?</strong> Install Vouchr for
              iOS to re-confirm in hardware custody (Face ID + Secure Enclave) and add government-ID and Live Vouch —
              the top trust tier recruiters trust most.
              <div style={{ marginTop: '0.75rem' }}>
                <a className="btn btn--secondary btn--block" href="https://tryvouchr.com">
                  Get the iOS app
                </a>
              </div>
              <p style={{ marginTop: '0.85rem', marginBottom: 0 }}>
                No iPhone, or prefer the browser? You can also{' '}
                <a href="https://tryvouchr.com/app" style={{ color: 'var(--c-accent)', fontWeight: 600 }}>
                  use Vouchr on the web
                </a>{' '}
                — quicker to set up, but without Face&nbsp;ID liveness and a hardware key it caps at Tier&nbsp;2, so it
                can't verify you as fully.
              </p>
            </div>
          </>
        ) : stage === 'holder' ? (
          <>
            <h1 className="auth__title">Hi {info.name} — let's set you up</h1>
            <p className="auth__sub">
              {invitedBy}prove you're a real person. First, secure this verification to your device with a passkey —
              it's what lets your Verified Mark travel with you, privately.
            </p>
            <div className="auth__form">
              <Button type="button" variant="primary" size="lg" block loading={loading} onClick={onBindKey}>
                Use a passkey on this device
              </Button>
              <button
                type="button"
                className="auth__linkbtn"
                onClick={() => {
                  setError('')
                  setStage('phone')
                }}
              >
                Skip for now
              </button>
              {error && <p className="auth__sub" style={{ color: 'var(--c-error)' }}>{error}</p>}
            </div>
            <p className="auth__note" style={{ display: 'flex', gap: '0.45rem', alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--cred-live)', flex: 'none', display: 'flex', marginTop: 1 }}>
                <IconShield size={14} />
              </span>
              A web passkey reaches Tier 2. The iOS app's hardware key reaches the top tier — you can upgrade anytime.
            </p>
          </>
        ) : (
          <>
            <h1 className="auth__title">
              {stage === 'phone' ? `${info.name} — verify your phone` : 'Enter your code'}
            </h1>
            <p className="auth__sub">
              {stage === 'phone'
                ? 'You do this yourself, on your own device. The recruiter never sees your number — only that it checked out.'
                : `We texted a 6-digit code to ${maskedPhone}. Enter it below to finish.`}
            </p>

            {stage === 'phone' ? (
              <form className="auth__form" onSubmit={onSendCode} noValidate>
                <Input
                  id="sv-phone"
                  label="Mobile number"
                  type="tel"
                  autoComplete="tel"
                  placeholder="+1 555 123 4567"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value)
                    if (error) setError('')
                  }}
                  error={error || undefined}
                />
                <Button type="submit" variant="primary" size="lg" block loading={loading}>
                  Text me a code
                </Button>
              </form>
            ) : (
              <form className="auth__form" onSubmit={onConfirm} noValidate>
                <Input
                  id="sv-code"
                  label="6-digit code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value)
                    if (error) setError('')
                  }}
                  error={error || undefined}
                />
                <Button type="submit" variant="primary" size="lg" block loading={loading}>
                  Verify phone
                </Button>
                <button
                  type="button"
                  className="auth__linkbtn"
                  onClick={() => {
                    setStage('phone')
                    setCode('')
                    setError('')
                  }}
                >
                  Use a different number
                </button>
              </form>
            )}

            <p className="auth__note" style={{ display: 'flex', gap: '0.45rem', alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--cred-live)', flex: 'none', display: 'flex', marginTop: 1 }}>
                {stage === 'phone' ? <IconShield size={14} /> : <IconLock size={14} />}
              </span>
              Checked for fraud risk. Your number is used only to verify it’s really you.
            </p>
          </>
        )}
      </Card>
      <Footer />
    </div>
  )
}
